import { SnowplowTracker } from "@/helpers/snowplow";

import type { AgentResponse } from "../../@types/agentcore";

/**
 * Configuration for initializing an AI agent session.
 */
export interface AIAgentSessionConfig {
  sessionId: string;
  actorId: string;
}

/** localStorage key for the resumable session, scoped to a person + TTL. */
const SESSION_CACHE_KEY = "aiAgentSession";

/** Stop/restart resume window. */
const SESSION_RESUME_WINDOW_MS = 48 * 60 * 60 * 1000; // 48 hours

/** AgentCore Runtime rejects a runtimeSessionId shorter than 33 chars. */
const MIN_SESSION_ID_LENGTH = 33;

/** A resumable session persisted to localStorage. */
interface CachedSession {
  personId: string;
  sessionId: string;
  expiresAt: number;
}

/**
 * Terminal agent states that indicate workflow completion
 */
const TERMINAL_STATES = [
  "COMPLETED",
  "FAILED",
  "TIMEOUT",
  "CANCELLED",
] as const;

/**
 * Helper class for managing AI agent session initialization.
 * Handles generation of session IDs and actor IDs with proper fallback logic.
 */
export class AIAgentSessionHelper {
  /**
   * Check if agent response state is terminal (COMPLETED, FAILED, or TIMEOUT).
   * Terminal states indicate the workflow has ended and metrics should be emitted.
   *
   * @param response - Agent response to check
   * @returns true if state is terminal, false otherwise
   */
  public static isTerminalState(response: AgentResponse): boolean {
    return TERMINAL_STATES.includes(
      response.state as (typeof TERMINAL_STATES)[number]
    );
  }
  /**
   * Resume the persisted sessionId when the same person restarts within the
   * resume window, else mint a fresh one. Scoped per-person and bounded because
   * reuse threads onto the prior AgentCore Memory history and aggregate row.
   */
  public static createSession(): AIAgentSessionConfig {
    const actorId = this.resolveActorId();
    const sessionId = this.resolveSessionId(actorId);
    return { sessionId, actorId };
  }

  /**
   * Actor ID: Cognito userId, else the still-valid cached personId, else a
   * generated UUID. Reusing the cached personId keeps the actorId stable across
   * calls so resume works even without a userId (the invoke is JWT-gated, so
   * this path is defensive). UUID and userId both satisfy the AgentCore Memory
   * actorId charset; hostname is not used because its `.` separators violate it.
   */
  private static resolveActorId(): string {
    return this.knownActorId() ?? crypto.randomUUID();
  }

  /**
   * Whether {@link createSession} would attribute to a real person rather than fall
   * back to a random UUID.
   *
   * For callers that can afford to wait for the tracker. A journey recorded under a
   * generated actor is unreachable in the review dashboard, which is worse than one
   * that started a few seconds later: it looks captured while being unfindable.
   */
  public static hasResolvedActor(): boolean {
    return this.knownActorId() !== undefined;
  }

  /**
   * The actor a real person is identified by, or undefined when none is known yet.
   *
   * Shared with {@link resolveActorId} so the expiry rule cannot drift: a check that
   * trusted a lapsed cache would report a resolved actor for a session that then
   * minted a random one.
   */
  private static knownActorId(): string | undefined {
    if (SnowplowTracker.userId) {
      return SnowplowTracker.userId;
    }
    const cached = this.readCachedSession();
    return cached && cached.expiresAt > Date.now()
      ? cached.personId
      : undefined;
  }

  /**
   * Return the cached sessionId when it belongs to {@link actorId} and is still
   * within the resume window; otherwise mint, persist, and return a fresh one.
   */
  private static resolveSessionId(actorId: string): string {
    const cached = this.readCachedSession();
    if (
      cached &&
      cached.personId === actorId &&
      cached.expiresAt > Date.now() &&
      cached.sessionId.length >= MIN_SESSION_ID_LENGTH
    ) {
      return cached.sessionId;
    }

    const sessionId = crypto.randomUUID();
    this.writeCachedSession({
      personId: actorId,
      sessionId,
      expiresAt: Date.now() + SESSION_RESUME_WINDOW_MS,
    });
    return sessionId;
  }

  /** Read the persisted session, or undefined if absent, malformed, or localStorage is unavailable. */
  private static readCachedSession(): CachedSession | undefined {
    try {
      const raw = localStorage.getItem(SESSION_CACHE_KEY);
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as Partial<CachedSession>;
      if (
        typeof parsed.personId === "string" &&
        typeof parsed.sessionId === "string" &&
        typeof parsed.expiresAt === "number"
      ) {
        return parsed as CachedSession;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }

  /** Persist the session to localStorage; a no-op if localStorage is unavailable. */
  private static writeCachedSession(session: CachedSession): void {
    try {
      localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(session));
    } catch {
      // localStorage unavailable; fall back to a non-persisted session.
    }
  }
}
